import { AxiosResponse } from 'axios';
import {
  SET_ERRORMSG,
  SET_EXPORT_DATASET_FIELDS,
  SET_EXPORT_DATASETS,
  SET_EXPORT_RUN,
  SET_EXPORT_TEMPLATE,
  SET_EXPORT_TEMPLATES,
} from '@/store/mutation_types';
import {
  CLEAR_EXPORT_TEMPLATE_ISLOADING,
  DELETE_EXPORT_TEMPLATE,
  DUPLICATE_EXPORT_TEMPLATE,
  EDIT_EXPORT_TEMPLATE,
  GENERATE_EXPORT_TEMPLATE,
  GET_EXPORT_DATASET_FIELDS,
  GET_EXPORT_DATASETS,
  GET_EXPORT_TEMPLATE,
  GET_EXPORT_TEMPLATES,
  REGISTER_EXPORT_TEMPLATE,
  RUN_EXPORT_TEMPLATE,
} from '@/store/action_types';
import ExportTemplate from '@/services/export_template';
import { Module } from 'vuex';
import {
  EditExportTemplate,
  ExportDatasetCatalog,
  ExportDatasetFieldList,
  ExportRunOutput,
  GenerateExportTemplate,
  GetExportDatasetFields,
  GetExportDatasets,
  GetExportTemplate,
  GetExportTemplates,
  RegisterExportTemplate,
  RunExportTemplate,
} from '@/dtos/export_template.dto';
import type { ApiExportTemplate, ExportPreview } from '@/utils/export_templates';

/**
 * Export templates for every module: the saved templates, the dataset catalog and field lists the
 * builder works from (cached), and template runs (preview or file download).
 *
 * Contract used by the settings pages, the builder and the run dialog:
 * - GET_EXPORT_TEMPLATES { module?, dataset? } -> ApiExportTemplate[] | false   (getter exportTemplates)
 * - GET_EXPORT_TEMPLATE { id } -> ApiExportTemplate | false                     (getter exportTemplate)
 * - REGISTER_ / EDIT_EXPORT_TEMPLATE payload -> saved template | false
 * - DELETE_EXPORT_TEMPLATE id -> { id } | false; DUPLICATE_EXPORT_TEMPLATE id -> the copy | false
 * - GET_EXPORT_DATASETS { force? } -> { modules, datasets } | false             (getter exportDatasets)
 * - GET_EXPORT_DATASET_FIELDS { dataset, force? } -> { dataset, groups, fields } | false
 *   (getter exportDatasetFields: { [datasetKey]: { data, isLoading } })
 * - RUN_EXPORT_TEMPLATE { id?, template?, filters, output, limit? }: a preview never shows the global
 *   error toast and resolves to ExportPreview | { error }; a file resolves to the axios response
 *   (Blob body) | false, with the usual toast on failure. Loading state: getter exportRun.
 * - GENERATE_EXPORT_TEMPLATE { id, payrollPeriodId } (legacy payroll download) -> response | false
 */

interface Loadable<T> {
  data: T | null;
  isLoading: boolean;
}

interface FieldsEntry extends Loadable<ExportDatasetFieldList> {
  /** organization the list belongs to; another sign-in in the same tab must not reuse it */
  scope: string | null;
  loadedAt: number;
}

interface ExportTemplateState {
  exportTemplate: Loadable<ApiExportTemplate>;
  exportTemplates: Loadable<ApiExportTemplate[]> & { query: GetExportTemplates; scope: string | null };
  exportDatasets: Loadable<ExportDatasetCatalog> & { scope: string | null };
  exportDatasetFields: Record<string, FieldsEntry>;
  exportRun: { previews: number; files: number };
}

/** Dynamic fields (allowance types, application questions, ...) change rarely; refetch after a minute. */
const FIELDS_TTL_MS = 60 * 1000;
const RUN_ERRORS: Record<ExportRunOutput, { failed: string; timeout: string }> = {
  preview: { failed: 'The preview could not be generated.', timeout: 'The preview took too long. Narrow the filters and try again.' },
  file: { failed: 'The export could not be created. Try again.', timeout: 'The export took too long. Narrow the filters and try again.' },
};
const NETWORK_ERROR = 'Could not reach the server. Check your connection and try again.';

/** Requests in flight, shared so components mounting together do not fetch the same thing twice. */
const inflight: { datasets: Promise<ExportDatasetCatalog | false> | null; fields: Record<string, Promise<ExportDatasetFieldList | false>> } = {
  datasets: null,
  fields: {},
};
/** Only the latest list request may write the list (a slow earlier one must not overwrite it). */
let listRequest = 0;

const organizationScope = (rootGetters: any): string | null => rootGetters.account?.data?.organizationId ?? null;

/** The dataset catalog depends on the organization and on the modules it is licensed for. */
const catalogScope = (rootGetters: any): string => `${organizationScope(rootGetters) ?? ''}|${(rootGetters.enabledModules ?? []).join(',')}`;

/** Runs a request; a request that throws (no answer from the server) counts as a failure. */
const attempt = async (request: () => Promise<AxiosResponse>): Promise<AxiosResponse | undefined> => {
  try {
    return await request();
  } catch (error) {
    return undefined;
  }
};

const okData = (result: AxiosResponse | undefined): any => (result && result.data?.status ? result.data.data : undefined);

const reportFailure = (commit: any, result: AxiosResponse | undefined) => {
  if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
};

const idOf = (payload: string | { id: string }): string => (typeof payload === 'string' ? payload : payload?.id);

/** Whether a template belongs in a list fetched with `query`. */
const matchesQuery = (template: ApiExportTemplate, query: GetExportTemplates): boolean =>
  (!query.module || template.module === query.module) && (!query.dataset || template.dataset === query.dataset);

/** Message for a failed run: the API's own words when it sent any. */
const runErrorOf = (error: any, output: ExportRunOutput): string => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (error?.code === 'ECONNABORTED') return RUN_ERRORS[output].timeout;
  if (error && !error.response) return NETWORK_ERROR;
  return RUN_ERRORS[output].failed;
};

const exportTemplate: Module<ExportTemplateState, any> = {
  state: {
    exportTemplate: { data: null, isLoading: false },
    exportTemplates: { data: null, isLoading: false, query: {}, scope: null },
    exportDatasets: { data: null, isLoading: false, scope: null },
    exportDatasetFields: {},
    exportRun: { previews: 0, files: 0 },
  },
  getters: {
    exportTemplate: (state) => state.exportTemplate,
    exportTemplates: (state) => state.exportTemplates,
    exportDatasets: (state) => state.exportDatasets,
    exportDatasetFields: (state) => state.exportDatasetFields,
    exportRun: (state) => ({
      isLoading: state.exportRun.previews + state.exportRun.files > 0,
      previewing: state.exportRun.previews > 0,
      exporting: state.exportRun.files > 0,
    }),
  },
  mutations: {
    [SET_EXPORT_TEMPLATE]: (state, payload: { data?: ApiExportTemplate | null; isLoading: boolean }) => {
      if (payload.data !== undefined) state.exportTemplate.data = payload.data;
      state.exportTemplate.isLoading = payload.isLoading;
    },
    /**
     * `data` replaces the list; `upsert` / `remove` keep an already loaded list in step with a save,
     * copy or delete (newest first, like the API orders it).
     */
    [SET_EXPORT_TEMPLATES]: (
      state,
      payload: {
        data?: ApiExportTemplate[] | null;
        query?: GetExportTemplates;
        scope?: string | null;
        isLoading?: boolean;
        upsert?: ApiExportTemplate;
        remove?: string;
      }
    ) => {
      const list = state.exportTemplates;
      if (payload.data !== undefined) list.data = payload.data;
      if (payload.query !== undefined) list.query = payload.query;
      if (payload.scope !== undefined) list.scope = payload.scope;
      if (payload.upsert && list.data) {
        const saved = payload.upsert;
        const rest = list.data.filter((t) => t.id !== saved.id);
        list.data = matchesQuery(saved, list.query) ? [saved, ...rest] : rest;
      }
      if (payload.remove && list.data) list.data = list.data.filter((t) => t.id !== payload.remove);
      if (payload.isLoading !== undefined) list.isLoading = payload.isLoading;
    },
    [SET_EXPORT_DATASETS]: (state, payload: { data?: ExportDatasetCatalog | null; scope?: string | null; isLoading: boolean }) => {
      if (payload.data !== undefined) state.exportDatasets.data = payload.data;
      if (payload.scope !== undefined) state.exportDatasets.scope = payload.scope;
      state.exportDatasets.isLoading = payload.isLoading;
    },
    [SET_EXPORT_DATASET_FIELDS]: (state, payload: { dataset: string; data?: ExportDatasetFieldList | null; scope?: string | null; isLoading: boolean }) => {
      const entry: FieldsEntry = state.exportDatasetFields[payload.dataset] ?? { data: null, isLoading: false, scope: null, loadedAt: 0 };
      if (payload.data !== undefined) {
        entry.data = payload.data;
        entry.loadedAt = payload.data ? Date.now() : 0;
      }
      if (payload.scope !== undefined) entry.scope = payload.scope;
      entry.isLoading = payload.isLoading;
      state.exportDatasetFields[payload.dataset] = entry;
    },
    [SET_EXPORT_RUN]: (state, payload: { output: ExportRunOutput; delta: 1 | -1 }) => {
      const key = payload.output === 'file' ? 'files' : 'previews';
      state.exportRun[key] = Math.max(0, state.exportRun[key] + payload.delta);
    },
  },
  actions: {
    [GET_EXPORT_TEMPLATES]: async ({ commit, state, rootGetters }, payload: GetExportTemplates = {}) => {
      const query: GetExportTemplates = { module: payload?.module || undefined, dataset: payload?.dataset || undefined };
      const scope = organizationScope(rootGetters);
      const request = ++listRequest;
      // Another sign-in in the same tab must not see the previous organization's list while loading.
      if (state.exportTemplates.scope !== scope) commit(SET_EXPORT_TEMPLATES, { data: null, scope });
      commit(SET_EXPORT_TEMPLATES, { isLoading: true });

      const result = await attempt(() => ExportTemplate.getExportTemplates(query));
      const data = okData(result);
      if (request !== listRequest) return data ?? false;

      if (data !== undefined) {
        commit(SET_EXPORT_TEMPLATES, { data, query, isLoading: false });
        return data as ApiExportTemplate[];
      }
      commit(SET_EXPORT_TEMPLATES, { isLoading: false });
      reportFailure(commit, result);
      return false;
    },
    [GET_EXPORT_TEMPLATE]: async ({ commit }, payload: GetExportTemplate) => {
      commit(SET_EXPORT_TEMPLATE, { data: null, isLoading: true });

      const result = await attempt(() => ExportTemplate.getExportTemplates({ id: payload?.id }));
      const data = okData(result);
      const template: ApiExportTemplate | null = Array.isArray(data) ? data[0] ?? null : null;
      commit(SET_EXPORT_TEMPLATE, { data: template, isLoading: false });
      if (data === undefined) reportFailure(commit, result);
      return template ?? false;
    },
    [REGISTER_EXPORT_TEMPLATE]: async ({ commit }, payload: RegisterExportTemplate) => {
      commit(SET_EXPORT_TEMPLATE, { isLoading: true });

      const result = await attempt(() => ExportTemplate.registerExportTemplate(payload));
      const data = okData(result);
      if (data) {
        commit(SET_EXPORT_TEMPLATE, { data, isLoading: false });
        commit(SET_EXPORT_TEMPLATES, { upsert: data });
        return data as ApiExportTemplate;
      }
      commit(SET_EXPORT_TEMPLATE, { isLoading: false });
      reportFailure(commit, result);
      return false;
    },
    [EDIT_EXPORT_TEMPLATE]: async ({ commit }, payload: EditExportTemplate) => {
      commit(SET_EXPORT_TEMPLATE, { isLoading: true });

      const result = await attempt(() => ExportTemplate.editExportTemplate(payload));
      const data = okData(result);
      if (data) {
        commit(SET_EXPORT_TEMPLATE, { data, isLoading: false });
        commit(SET_EXPORT_TEMPLATES, { upsert: data });
        return data as ApiExportTemplate;
      }
      commit(SET_EXPORT_TEMPLATE, { isLoading: false });
      reportFailure(commit, result);
      return false;
    },
    [DELETE_EXPORT_TEMPLATE]: async ({ commit, state }, payload: string | { id: string }) => {
      const id = idOf(payload);
      const result = await attempt(() => ExportTemplate.deleteExportTemplate(id));
      const data = okData(result);
      if (data !== undefined) {
        commit(SET_EXPORT_TEMPLATES, { remove: id });
        if (state.exportTemplate.data?.id === id) commit(SET_EXPORT_TEMPLATE, { data: null, isLoading: false });
        return data || { id };
      }
      reportFailure(commit, result);
      return false;
    },
    [DUPLICATE_EXPORT_TEMPLATE]: async ({ commit }, payload: string | { id: string }) => {
      const result = await attempt(() => ExportTemplate.duplicateExportTemplate(idOf(payload)));
      const data = okData(result);
      if (data) {
        commit(SET_EXPORT_TEMPLATES, { upsert: data });
        return data as ApiExportTemplate;
      }
      reportFailure(commit, result);
      return false;
    },
    [GET_EXPORT_DATASETS]: async ({ commit, state, rootGetters }, payload: GetExportDatasets = {}) => {
      const scope = catalogScope(rootGetters);
      const cached = state.exportDatasets;
      if (!payload?.force && cached.data && cached.scope === scope) return cached.data;
      if (!payload?.force && inflight.datasets) return inflight.datasets;

      const request = (async (): Promise<ExportDatasetCatalog | false> => {
        commit(SET_EXPORT_DATASETS, { isLoading: true });
        const result = await attempt(() => ExportTemplate.getExportDatasets());
        const data = okData(result);
        if (data) {
          const catalog: ExportDatasetCatalog = { modules: data.modules ?? [], datasets: data.datasets ?? [] };
          commit(SET_EXPORT_DATASETS, { data: catalog, scope, isLoading: false });
          return catalog;
        }
        commit(SET_EXPORT_DATASETS, { isLoading: false });
        reportFailure(commit, result);
        return false;
      })();
      inflight.datasets = request;
      try {
        return await request;
      } finally {
        if (inflight.datasets === request) inflight.datasets = null;
      }
    },
    [GET_EXPORT_DATASET_FIELDS]: async ({ commit, state, rootGetters }, payload: GetExportDatasetFields) => {
      const key = payload?.dataset;
      if (!key) return false;
      const scope = organizationScope(rootGetters);
      const cached = state.exportDatasetFields[key];
      const fresh = cached?.data && cached.scope === scope && Date.now() - cached.loadedAt < FIELDS_TTL_MS;
      if (!payload.force && fresh) return cached.data;
      if (!payload.force && key in inflight.fields) return inflight.fields[key];

      const request = (async (): Promise<ExportDatasetFieldList | false> => {
        commit(SET_EXPORT_DATASET_FIELDS, { dataset: key, isLoading: true });
        const result = await attempt(() => ExportTemplate.getExportDatasetFields(key));
        const data = okData(result);
        if (data) {
          const list: ExportDatasetFieldList = { dataset: data.dataset, groups: data.groups ?? [], fields: data.fields ?? [] };
          commit(SET_EXPORT_DATASET_FIELDS, { dataset: key, data: list, scope, isLoading: false });
          return list;
        }
        commit(SET_EXPORT_DATASET_FIELDS, { dataset: key, isLoading: false });
        reportFailure(commit, result);
        return false;
      })();
      inflight.fields[key] = request;
      try {
        return await request;
      } finally {
        if (inflight.fields[key] === request) delete inflight.fields[key];
      }
    },
    [RUN_EXPORT_TEMPLATE]: async ({ commit }, payload: RunExportTemplate) => {
      const output: ExportRunOutput = payload?.output === 'file' ? 'file' : 'preview';
      const body: RunExportTemplate = { ...payload, output };
      commit(SET_EXPORT_RUN, { output, delta: 1 });
      try {
        const result: AxiosResponse = await ExportTemplate.runExportTemplate(body);
        if (output === 'file') return result;
        const data = okData(result);
        return data ? (data as ExportPreview) : { error: result?.data?.message || result?.data?.msg || RUN_ERRORS.preview.failed };
      } catch (error: any) {
        // The request is silent: a preview hands the message to the caller, a file shows the usual toast.
        const message = runErrorOf(error, output);
        if (output === 'preview') return { error: message };
        commit(SET_ERRORMSG, message);
        return false;
      } finally {
        commit(SET_EXPORT_RUN, { output, delta: -1 });
      }
    },
    [GENERATE_EXPORT_TEMPLATE]: async ({ commit }, payload: GenerateExportTemplate) => {
      commit(SET_EXPORT_RUN, { output: 'file', delta: 1 });
      try {
        const result = await attempt(() => ExportTemplate.generateExportTemplate(payload));
        return result || false;
      } finally {
        commit(SET_EXPORT_RUN, { output: 'file', delta: -1 });
      }
    },
    [CLEAR_EXPORT_TEMPLATE_ISLOADING]: ({ commit }) => {
      commit(SET_EXPORT_TEMPLATE, { isLoading: false });
      commit(SET_EXPORT_TEMPLATES, { isLoading: false });
      commit(SET_EXPORT_DATASETS, { isLoading: false });
    },
  },
};

export default exportTemplate;
